import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';

import { TranslateService } from '@ngx-translate/core';

import {
  CustomerThread,
  MessageLog,
  MessageGroup
} from './typings/customer-messages.customer-thread-params';

import sendIcon from 'icons/send-icon.svg';

type MenuAction = 'view_customer' | 'open_latest_invoice' | 'mark_unread';
type LinkPart   = { text: string; href?: string | null };

@Component({
  selector   : 'wor-customer-messages-message-thread',
  styles: [require('./customer-messages.message-thread.component.scss')],
  template: require('./customer-messages.message-thread.component.html')
})

export class CustomerMessagesMessageThreadComponent implements OnChanges, AfterViewInit {
  @Input() disabled                                   = false;
  @Input() inputPlaceholder                           = this.translateService.instant('COMPANY_SPACE.INTEGRATIONS.CUSTOMER_MESSAGES.INPUT_MESSAGE_PLACEHOLDER');
  @Input() jobCardText        : string | null         = null;
  @Input() outgoingBubbleColor                        = '#0aa5c9';
  @Input() outgoingDisplayName: string | null         = null;
  @Input() thread             : CustomerThread | null = null;

  @Output() back        = new EventEmitter<void>();
  @Output() loadMore    = new EventEmitter<void>();
  @Output() menuAction  = new EventEmitter<MenuAction>();
  @Output() sendMessage = new EventEmitter<string>();

  @ViewChild('bodyEl', { static: false }) bodyEl?: ElementRef<HTMLElement>;

  beforePrependScrollHeight = 0;
  draft                     = '';
  didInitScroll             = false;
  grouped: MessageGroup[]   = [];
  menuOpen                  = false;
  pendingPrependAdjust      = false;
  sendIcon                  = sendIcon;

  private regexUrl   = /(https?:\/\/[^\s]+(?:[\r\n]+[^\s]+)*)/gi;
  private regexTrail = /[)\].,;:!?}]+$/;

  constructor(
    private hostEl          : ElementRef<HTMLElement>,
    private translateService: TranslateService,
  ) {}

  dateKey ( dateTime : string | null | undefined ) : string {
    const ms = this.parseDates(dateTime);
    if (!ms) {
      return 'unknown';
    }
    const d   = new Date(ms);
    const y   = d.getFullYear();
    const m   = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  dateLabelFromKey ( key : string ) : string {
    if (key === 'unknown') {
      return '';
    }
    const [y, m, d] = key
      .split('-')
      .map(x => parseInt(x, 10));
    const dt = new Date(y, (m - 1), d);

    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day  : '2-digit',
      year : 'numeric'
    }).format(dt);
  }

  displayOutgoingName ( msg : MessageLog ) : string {
    const override = (this.outgoingDisplayName || '').trim();
    if (override) {
      return override;
    }
    return (msg.user_name || 'User').trim();
  }

  extractLink ( message : string | null | undefined ) : LinkPart[] {
    const text = String( message || '' );
    if (!text) {
      return [{ text: '' }];
    }

    const noBreaks = ( str : string ) => str.replace(/[\r\n]+/g, '');

    // If it starts with http(s)
    const trimmed = text.trim();

    if (/^https?:\/\//i.test(trimmed)) {
      return [{ text: trimmed, href: encodeURI(noBreaks(trimmed)) }];
    }

    const out : LinkPart[] = [];
    let last = 0;

    for (const m of text.matchAll(this.regexUrl)) {
      const start = m.index ?? 0;
      const raw   = m[0];

      if (start > last) {
        out.push({ text: text.slice(last, start) });
      }

      const tail = raw.match(this.regexTrail)?.[0] || '';
      const core = tail
        ? raw.slice(0, -tail.length)
        : raw;

      out.push({
        text: core,
        href: noBreaks(core)
      });
      if (tail) {
        out.push({ text: tail });
      }

      last = start + raw.length;
    }

    out.push({ text: text.slice(last) });
    return out;
  }

  get showLoadMore () : boolean {
    const thread = this.thread;
    const hasMessages = Array.isArray(thread?.message_logs) && thread?.message_logs.length > 0;
    return !!thread && hasMessages && thread.is_last !== true && thread.next_offset != null;
  }

  get customerDisplayName () : string {
    const thread = this.thread;
    const first  = (thread.first_name || '').trim();
    const last   = (thread.last_name || '').trim();
    const full   = `${first} ${last}`.trim();

    if (full) {
      return full;
    }

    if (thread.company_name) {
      return String(thread.company_name);
    }
    return `Unknown Number ${thread.customer_id?.slice(0, 6) ?? ''}`.trim();
  }

  get customerInitials () : string {
    const name = this.customerDisplayName.trim();
    if (!name) {
      return 'C';
    }

    const parts =
      name
      .split(/\s+/)
      .filter(Boolean);
    if (parts.length === 1) {
      return parts[0].slice(0, 2).toUpperCase();
    }
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }

  getAvatarThemeClass () : string {
    const thread = this.thread;
    const key = String(thread?.customer_id || this.customerDisplayName || '');
    const n = this.hashToInt(key) % 20;
    return `avatar-theme-${n}`;
  }

  hashToInt ( str : string ) : number {
    let h = 0;
    for (let i = 0; i < str.length; i++) {
      h = ((h << 5) - h) + str.charCodeAt(i);
      h |= 0;
    }
    return Math.abs(h);
  }

  handleScrollAfterThreadUpdate () : void {
    const el = this.bodyEl?.nativeElement;
    if (!el) {
      return;
    }

    if (this.pendingPrependAdjust) {
      setTimeout(() => {
        const elem = this.bodyEl?.nativeElement;
        if ( !elem ) {
          return;
        }

        const after    = elem.scrollHeight;
        const delta    = after - this.beforePrependScrollHeight;
        elem.scrollTop = elem.scrollTop + delta;

        this.pendingPrependAdjust      = false;
        this.beforePrependScrollHeight = 0;
      });
      return;
    }

    if ( !this.didInitScroll ) {
      this.didInitScroll = true;
      this.scrollToBottomSoon();
      return;
    }

    // default: new messages appended => stay at bottom
    this.scrollToBottomSoon();
  }

  isOutgoing ( msg : MessageLog ) : boolean {
    if ( (msg.direction || '').toLowerCase() === 'outbound' ) {
      return true;
    }
    if ( (msg.direction || '').toLowerCase() === 'inbound' ) {
      return false;
    }

    if (msg.user_id) {
      return true;
    }

    // user_name exists and is not "System"
    if (msg.user_name && String(msg.user_name).trim()) {
      return true;
    }

    return false;
  }

  ngAfterViewInit () : void {
    this.scrollToBottomSoon();
  }

  ngOnChanges ( changes : SimpleChanges ) : void {
    if (changes.thread) {
      const prev = changes.thread.previousValue as CustomerThread | null;
      const next = changes.thread.currentValue as CustomerThread | null;

      if (prev?.customer_id !== next?.customer_id) {
        this.didInitScroll = false;
        this.pendingPrependAdjust = false;
        this.draft = '';
        this.menuOpen = false;
      }

      this.rebuildGroups();
      this.handleScrollAfterThreadUpdate();
    }
  }

  onLoadMoreClicked () : void {
    if (this.disabled) {
      return;
    }

    const el = this.bodyEl?.nativeElement;
    if (el) {
      this.pendingPrependAdjust = true;
      this.beforePrependScrollHeight = el.scrollHeight;
    }

    this.loadMore.emit();
  }

  onBack () : void {
    this.back.emit();
  }

  onMenu ( action : MenuAction ) : void {
    this.menuOpen = false;
    this.menuAction.emit(action);
  }

  onSendClicked () : void {
    if (this.disabled) {
      return;
    }

    const txt = (this.draft || '').trim();
    if (!txt) {
      return;
    }

    this.sendMessage.emit(txt);
    this.draft = '';
  }

  @HostListener('document:click', ['$event'])
  onDocClick ( event : MouseEvent ) : void {
    if (!this.menuOpen) {
      return;
    }

    const target = event.target as HTMLElement | null;
    if (!target) {
      return;
    }

    const inside = this.hostEl.nativeElement.contains(target);
    if (!inside) {
      this.menuOpen = false;
    }
  }

  parseDates ( dateTime : string | null | undefined ) : number {
    if (!dateTime) {
      return 0;
    }

    if ( dateTime.includes('Z') || /[+-]\d{2}:\d{2}$/.test(dateTime) ) {
      const dt = Date.parse(dateTime);
      return Number.isFinite(dt) ? dt : 0;
    }

    const isoLocal = dateTime.includes('T') ? dateTime : dateTime.replace(' ', 'T');

    const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?/.exec(isoLocal);
    if (!m) {
      const t = Date.parse(isoLocal);
      return Number.isFinite(t) ? t : 0;
    }

    const year = parseInt(m[1], 10);
    const mon  = parseInt(m[2], 10) - 1;
    const day  = parseInt(m[3], 10);
    const hh   = parseInt(m[4], 10);
    const mm   = parseInt(m[5], 10);
    const ss   = parseInt(m[6] || '0', 10);

    return new Date(year, mon, day, hh, mm, ss).getTime();
  }

  rebuildGroups () : void {
    const logs = (this.thread?.message_logs || []).slice();

    // sort by time
    logs.sort((a, b) => this.parseDates(a.created_on) - this.parseDates(b.created_on));

    const map = new Map<string, MessageLog[]>();
    for (const msg of logs) {
      const key = this.dateKey(msg.created_on);
      if (!map.has(key)) {
        map.set(key, []);
      }
      map.get(key)!.push(msg);
    }

    const keys = Array.from(map.keys()).sort();
    this.grouped = keys.map(k => ({
      key  : k,
      label: this.dateLabelFromKey(k),
      items: map.get(k) || []
    }));
  }

  scrollToBottomSoon () : void {
    setTimeout(() => {
      const el = this.bodyEl?.nativeElement;
      if (!el) {
        return;
      }
      el.scrollTop = el.scrollHeight;
    });
  }

  toggleMenu () : void {
    if (this.disabled) {
      return;
    }
    this.menuOpen = !this.menuOpen;
  }

  timeLabel ( dateTime : string | null | undefined ) : string {
    const ms = this.parseDates(dateTime);
    if (!ms) {
      return '';
    }

    const d    = new Date(ms);
    const h    = d.getHours();
    const m    = d.getMinutes();
    const ampm = h >= 12 ? 'pm' : 'am';
    const hh   = (h % 12) || 12;
    const mm   = String(m).padStart(2, '0');
    return `${hh}:${mm}${ampm}`;
  }

  trackByIndex ( idx : number ) : number {
    return idx;
  }

}
